import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, Home, Settings } from 'lucide-react';
import { toast } from 'sonner';

interface Message {
  id: string;
  sender: 'user' | 'ai';
  text: string;
  timestamp: Date;
}

export default function Chat() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get('mode') || 'story';
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [character, setCharacter] = useState<any>(null);
  const [playerName, setPlayerName] = useState('');

  useEffect(() => {
    const char = localStorage.getItem('selectedCharacter');
    const name = localStorage.getItem('playerName');
    if (char) setCharacter(JSON.parse(char));
    if (name) setPlayerName(name);

    // Welcome message
    if (char) {
      const parsedChar = JSON.parse(char);
      setMessages([
        {
          id: '1',
          sender: 'ai',
          text: `Hi ${name}! I'm ${parsedChar.name}. Let's have fun in ${mode} mode! What should we do?`,
          timestamp: new Date(),
        },
      ]);
    }
  }, [mode]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getSystemPrompt = () => {
    const prompts: Record<string, string> = {
      story: 'You are a creative storyteller helping a child create an engaging story. Keep responses short, fun, and age-appropriate. Ask questions to help develop the story.',
      trivia: 'You are a fun trivia host for kids. Ask one multiple-choice trivia question at a time. Keep questions fun and educational. Celebrate correct answers!',
      family: 'You are a friendly, fun companion for a child. Have engaging conversations about topics they suggest. Keep everything positive and age-appropriate.',
      adventure: 'You are an adventure guide. Create exciting, safe adventure scenarios for the child to explore. Keep it imaginative and fun.',
    };
    return prompts[mode] || prompts.story;
  };

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      sender: 'user',
      text: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: input,
          character: character?.name,
          mode,
          systemPrompt: getSystemPrompt(),
          playerName,
        }),
      });

      if (!response.ok) throw new Error('Failed to get response');

      const data = await response.json();
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        sender: 'ai',
        text: data.response,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      toast.error('Failed to get AI response. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen bg-gradient-to-b from-blue-300 via-purple-300 to-pink-300 flex flex-col">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur border-b shadow-sm p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="text-4xl">{character?.emoji}</div>
          <div>
            <h2 className="font-bold text-gray-800">{character?.name}</h2>
            <p className="text-sm text-gray-600 capitalize">{mode} Mode</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => navigate('/modes')}
            title="Back to modes"
          >
            <Home className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <Card
              className={`max-w-xs md:max-w-md px-4 py-3 ${
                msg.sender === 'user'
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                  : 'bg-white/95 text-gray-800'
              }`}
            >
              <p className="text-sm md:text-base">{msg.text}</p>
            </Card>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <Card className="bg-white/95 px-4 py-3">
              <div className="flex gap-2">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div>
              </div>
            </Card>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="bg-white/95 backdrop-blur border-t p-4">
        <div className="flex gap-2">
          <Input
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !loading && handleSendMessage()}
            disabled={loading}
            className="flex-1"
          />
          <Button
            onClick={handleSendMessage}
            disabled={loading || !input.trim()}
            className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
